<script setup>
/* eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text */
// M26.1 Final UI Polish — dashboard summary cards, advanced filters, sticky
// table header, bulk selection/actions, and CSV import/export/template.
// UI only: import/export/bulk-import are implemented client-side (CSV parse
// + generate) against the existing single-record create/update/destroy/
// mark_* endpoints looped one at a time — no new backend endpoint, no API
// shape change.
import { ref, reactive, computed, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAdmin } from 'dashboard/composables/useAdmin';
import unitInventoryAPI from 'dashboard/api/nexceo/unitInventory';
import { useProjectHeader } from 'dashboard/composables/nexceo/useProjectHeader';
import ProjectWorkspaceLayout from './ProjectWorkspaceLayout.vue';
import { projectWorkspaceTabs } from './projectWorkspaceTabs';

const route = useRoute();
const { accountId } = useAccount();
// M29.5.16A — create/update/destroy on units (pricing edit, import, delete)
// are entirely admin-gated server-side (UnitInventoriesController
// #check_admin_permission — not even team-leader tier), so `isAdmin` here
// is an exact match for the backend rule, not a conservative approximation.
// M29.5.18 (Final Agent Workspace) — status changes (available/hold/
// blocked/sold) used to be open to every project member; a plain agent is
// now read-only on inventory (view/search/filter only), so status mutation
// requires `isManagerOrAdmin` below, matching
// Nexceo::AccessPolicy#can_manage_unit_status? exactly.
const { isAdmin } = useAdmin();
const projectId = computed(() => Number(route.params.id));

const isStandalone = computed(() => route.name === 'nexceo_project_inventory');
const { project } = useProjectHeader(projectId);
// Defaults to "not privileged" until `project.my_role` resolves so status
// controls never flash visible for an agent while loading; isAdmin is
// already synchronous so an actual admin never sees that default.
const isManagerOrAdmin = computed(
  () => isAdmin.value || project.value?.my_role === 'project_manager'
);
const workspaceTabs = computed(() =>
  projectWorkspaceTabs({
    standalone: true,
    id: route.params.id,
    accountId: accountId.value,
    agentOnly: !isManagerOrAdmin.value,
  })
);

const units = ref([]);
const isLoading = ref(false);
const error = ref(null);
const showForm = ref(false);
const editingUnit = ref(null);
const isSaving = ref(false);
const saveError = ref(null);
const confirmDelete = ref(null);

// ── Filters (advanced) ────────────────────────────────────────────────────
const searchQuery = ref('');
const statusFilter = ref('');
const bhkFilter = ref('');
const towerFilter = ref('');
const unitTypeFilter = ref('');
const facingFilter = ref('');
const visibilityFilter = ref('');
const minPriceFilter = ref('');
const maxPriceFilter = ref('');
const showAdvancedFilters = ref(false);

const STATUS_OPTIONS = [
  { value: 'available', label: 'Available', color: '#10b981' },
  { value: 'hold', label: 'On Hold', color: '#f59e0b' },
  { value: 'blocked', label: 'Blocked', color: '#f97316' },
  { value: 'sold', label: 'Sold', color: '#ef4444' },
  { value: 'unavailable', label: 'Unavailable', color: '#94a3b8' },
  { value: 'landowner', label: 'Landowner', color: '#8b5cf6' },
];

const VISIBILITIES = [
  { value: 'internal', label: 'Internal Only' },
  { value: 'ai_summary_only', label: 'AI Summary Only' },
  { value: 'customer_shareable', label: 'Customer Shareable' },
];

const emptyForm = () => ({
  unit_no: '',
  tower: '',
  floor: '',
  unit_type: '',
  bhk: '',
  carpet_area: '',
  builtup_area: '',
  super_builtup_area: '',
  balcony_area: '',
  facing: '',
  price: '',
  price_display: '',
  status: 'available',
  visibility: 'internal',
  customer_shareable: false,
  notes: '',
});

const form = reactive(emptyForm());
// Room-by-room dimensions (Living/Dining, Kitchen, Bedroom 1/2/3, Bathroom
// 1/2, Balcony 1/2, ...) — a free-form {label, size} list rather than fixed
// per-room fields, since which rooms exist varies by BHK; the admin adds
// exactly the rows that apply to this unit. Read verbatim by the
// customer-facing Specifications tab (UnitDetailPanel.vue).
const specifications = ref([]);
function addSpecification() {
  specifications.value.push({ label: '', size: '' });
}
function removeSpecification(index) {
  specifications.value.splice(index, 1);
}

const filteredUnits = computed(() => {
  let list = units.value;
  if (statusFilter.value)
    list = list.filter(u => u.status === statusFilter.value);
  if (bhkFilter.value) list = list.filter(u => u.bhk === bhkFilter.value);
  if (towerFilter.value) list = list.filter(u => u.tower === towerFilter.value);
  if (unitTypeFilter.value)
    list = list.filter(u => u.unit_type === unitTypeFilter.value);
  if (facingFilter.value)
    list = list.filter(u => u.facing === facingFilter.value);
  if (visibilityFilter.value)
    list = list.filter(u => u.visibility === visibilityFilter.value);
  if (minPriceFilter.value)
    list = list.filter(
      u => Number(u.price || 0) >= Number(minPriceFilter.value)
    );
  if (maxPriceFilter.value)
    list = list.filter(
      u => Number(u.price || 0) <= Number(maxPriceFilter.value)
    );
  if (searchQuery.value.trim()) {
    const q = searchQuery.value.trim().toLowerCase();
    list = list.filter(u =>
      [u.unit_no, u.tower, u.unit_type, u.facing, u.notes]
        .filter(Boolean)
        .some(v => String(v).toLowerCase().includes(q))
    );
  }
  return list;
});

const hasActiveFilters = computed(
  () =>
    !!(
      searchQuery.value ||
      statusFilter.value ||
      bhkFilter.value ||
      towerFilter.value ||
      unitTypeFilter.value ||
      facingFilter.value ||
      visibilityFilter.value ||
      minPriceFilter.value ||
      maxPriceFilter.value
    )
);

function clearFilters() {
  searchQuery.value = '';
  statusFilter.value = '';
  bhkFilter.value = '';
  towerFilter.value = '';
  unitTypeFilter.value = '';
  facingFilter.value = '';
  visibilityFilter.value = '';
  minPriceFilter.value = '';
  maxPriceFilter.value = '';
}

const uniqueBhk = computed(() =>
  [...new Set(units.value.map(u => u.bhk).filter(Boolean))].sort()
);
const uniqueTower = computed(() =>
  [...new Set(units.value.map(u => u.tower).filter(Boolean))].sort()
);
const uniqueUnitType = computed(() =>
  [...new Set(units.value.map(u => u.unit_type).filter(Boolean))].sort()
);
const uniqueFacing = computed(() =>
  [...new Set(units.value.map(u => u.facing).filter(Boolean))].sort()
);

// ── Summary cards ─────────────────────────────────────────────────────────
const summary = computed(() => {
  const counts = {
    available: 0,
    hold: 0,
    blocked: 0,
    sold: 0,
    unavailable: 0,
    landowner: 0,
  };
  let totalValue = 0;
  units.value.forEach(u => {
    if (counts[u.status] !== undefined) counts[u.status] += 1;
    if (u.status === 'available') totalValue += Number(u.price || 0);
  });
  return { total: units.value.length, ...counts, totalValue };
});

function formatCr(v) {
  if (!v) return '₹0';
  if (v >= 10_000_000) return `₹${(v / 10_000_000).toFixed(2)}Cr`;
  if (v >= 100_000) return `₹${(v / 100_000).toFixed(2)}L`;
  return `₹${v.toLocaleString('en-IN')}`;
}

// M29.5.20A — status cards and the Available Value card are rendered as two
// visually separate rows (see template), so they're kept as two separate
// data sources rather than one flat list — folding "Available Value" back
// into this array is exactly the 5+1-wrap bug this refinement fixes.
function pctOfTotal(count) {
  if (!summary.value.total) return '0.00';
  return ((count / summary.value.total) * 100).toFixed(2);
}

const STATUS_SUMMARY_CARDS = computed(() => [
  {
    label: 'Total Units',
    value: summary.value.total,
    subtitle: 'All units in this project',
    icon: 'i-lucide-layout-grid',
    color: '#6366f1',
  },
  {
    label: 'Available',
    value: summary.value.available,
    subtitle: `${pctOfTotal(summary.value.available)}% of total units`,
    icon: 'i-lucide-check-circle-2',
    color: '#10b981',
  },
  {
    label: 'On Hold',
    value: summary.value.hold,
    subtitle: `${pctOfTotal(summary.value.hold)}% of total units`,
    icon: 'i-lucide-clock',
    color: '#f59e0b',
  },
  {
    label: 'Blocked',
    value: summary.value.blocked,
    subtitle: `${pctOfTotal(summary.value.blocked)}% of total units`,
    icon: 'i-lucide-ban',
    color: '#f97316',
  },
  {
    label: 'Sold',
    value: summary.value.sold,
    subtitle: `${pctOfTotal(summary.value.sold)}% of total units`,
    icon: 'i-lucide-key-round',
    color: '#ef4444',
  },
  {
    label: 'Landowner',
    value: summary.value.landowner,
    subtitle: `${pctOfTotal(summary.value.landowner)}% of total units`,
    icon: 'i-lucide-user-check',
    color: '#8b5cf6',
  },
]);

async function load() {
  isLoading.value = true;
  error.value = null;
  try {
    const { data } = await unitInventoryAPI.list(projectId.value);
    units.value = Array.isArray(data) ? data : [];
  } catch {
    error.value = 'Failed to load inventory.';
  } finally {
    isLoading.value = false;
  }
}

onMounted(load);

function openNew() {
  editingUnit.value = null;
  Object.assign(form, emptyForm());
  specifications.value = [];
  showForm.value = true;
  saveError.value = null;
}

function openEdit(unit) {
  editingUnit.value = unit;
  Object.assign(form, {
    unit_no: unit.unit_no ?? '',
    tower: unit.tower ?? '',
    floor: unit.floor ?? '',
    unit_type: unit.unit_type ?? '',
    bhk: unit.bhk ?? '',
    carpet_area: unit.carpet_area ?? '',
    builtup_area: unit.builtup_area ?? '',
    super_builtup_area: unit.super_builtup_area ?? '',
    balcony_area: unit.balcony_area ?? '',
    facing: unit.facing ?? '',
    price: unit.price ?? '',
    price_display: unit.price_display ?? '',
    status: unit.status ?? 'available',
    visibility: unit.visibility ?? 'internal',
    customer_shareable: unit.customer_shareable ?? false,
    notes: unit.notes ?? '',
  });
  specifications.value = (unit.specifications || []).map(s => ({ ...s }));
  showForm.value = true;
  saveError.value = null;
}

function closeForm() {
  showForm.value = false;
  editingUnit.value = null;
  saveError.value = null;
}

async function save() {
  if (!form.unit_no.trim()) {
    saveError.value = 'Unit number is required.';
    return;
  }
  isSaving.value = true;
  saveError.value = null;
  try {
    const payload = { ...form };
    if (payload.floor === '') payload.floor = null;
    if (payload.price === '') payload.price = null;
    if (payload.carpet_area === '') payload.carpet_area = null;
    if (payload.builtup_area === '') payload.builtup_area = null;
    if (payload.super_builtup_area === '') payload.super_builtup_area = null;
    if (payload.balcony_area === '') payload.balcony_area = null;
    payload.specifications = specifications.value.filter(
      s => s.label.trim() || s.size.trim()
    );

    if (editingUnit.value) {
      await unitInventoryAPI.update(
        projectId.value,
        editingUnit.value.id,
        payload
      );
    } else {
      await unitInventoryAPI.create(projectId.value, payload);
    }
    await load();
    closeForm();
  } catch (e) {
    const msgs = e?.response?.data?.errors;
    saveError.value = Array.isArray(msgs)
      ? msgs.join(', ')
      : (e?.response?.data?.error ?? 'Save failed.');
  } finally {
    isSaving.value = false;
  }
}

async function setStatus(unit, action) {
  try {
    const actionMap = {
      available: () => unitInventoryAPI.markAvailable(projectId.value, unit.id),
      hold: () => unitInventoryAPI.markHold(projectId.value, unit.id),
      blocked: () => unitInventoryAPI.markBlocked(projectId.value, unit.id),
      sold: () => unitInventoryAPI.markSold(projectId.value, unit.id),
    };
    await actionMap[action]();
    await load();
  } catch {
    error.value = 'Failed to update status.';
  }
}

async function deleteUnit(unit) {
  try {
    await unitInventoryAPI.destroy(projectId.value, unit.id);
    confirmDelete.value = null;
    await load();
  } catch {
    error.value = 'Failed to delete unit.';
  }
}

function statusColor(status) {
  return STATUS_OPTIONS.find(s => s.value === status)?.color ?? '#64748b';
}

function statusLabel(status) {
  return STATUS_OPTIONS.find(s => s.value === status)?.label ?? status;
}

function priceLabel(unit) {
  if (unit.price_display) return unit.price_display;
  if (!unit.price) return '—';
  return formatCr(Number(unit.price));
}

// ── Bulk selection + bulk actions ────────────────────────────────────────
const selectedIds = ref(new Set());
const isBulkWorking = ref(false);

const allVisibleSelected = computed(
  () =>
    filteredUnits.value.length > 0 &&
    filteredUnits.value.every(u => selectedIds.value.has(u.id))
);

function toggleSelect(id) {
  const next = new Set(selectedIds.value);
  if (next.has(id)) next.delete(id);
  else next.add(id);
  selectedIds.value = next;
}

function toggleSelectAllVisible() {
  const next = new Set(selectedIds.value);
  if (allVisibleSelected.value) {
    filteredUnits.value.forEach(u => next.delete(u.id));
  } else {
    filteredUnits.value.forEach(u => next.add(u.id));
  }
  selectedIds.value = next;
}

function clearSelection() {
  selectedIds.value = new Set();
}

async function bulkSetStatus(action) {
  isBulkWorking.value = true;
  const ids = [...selectedIds.value];
  try {
    await ids.reduce(async (chain, id) => {
      await chain;
      const unit = units.value.find(u => u.id === id);
      if (unit) await setStatus(unit, action);
    }, Promise.resolve());
    clearSelection();
  } finally {
    isBulkWorking.value = false;
  }
}

async function bulkDelete() {
  // eslint-disable-next-line no-restricted-globals, no-alert
  if (
    !window.confirm(
      `Delete ${selectedIds.value.size} selected unit(s)? This cannot be undone.`
    )
  )
    return;
  isBulkWorking.value = true;
  const ids = [...selectedIds.value];
  try {
    await ids.reduce(async (chain, id) => {
      await chain;
      await unitInventoryAPI.destroy(projectId.value, id);
    }, Promise.resolve());
    clearSelection();
    await load();
  } catch {
    error.value = 'Some units failed to delete.';
  } finally {
    isBulkWorking.value = false;
  }
}

// ── CSV export / template ────────────────────────────────────────────────
const CSV_COLUMNS = [
  { key: 'unit_no', label: 'Unit No' },
  { key: 'tower', label: 'Tower' },
  { key: 'floor', label: 'Floor' },
  { key: 'unit_type', label: 'Unit Type' },
  { key: 'bhk', label: 'BHK' },
  { key: 'carpet_area', label: 'Carpet Area' },
  { key: 'builtup_area', label: 'Builtup Area' },
  { key: 'super_builtup_area', label: 'Super Builtup Area' },
  { key: 'balcony_area', label: 'Balcony Area' },
  { key: 'facing', label: 'Facing' },
  { key: 'price', label: 'Price' },
  { key: 'price_display', label: 'Price Display' },
  { key: 'status', label: 'Status' },
  { key: 'visibility', label: 'Visibility' },
  { key: 'customer_shareable', label: 'Customer Shareable' },
  { key: 'notes', label: 'Notes' },
];

function csvEscape(value) {
  const s = value === null || value === undefined ? '' : String(value);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function rowsToCsv(rows) {
  const header = CSV_COLUMNS.map(c => csvEscape(c.label)).join(',');
  const lines = rows.map(row =>
    CSV_COLUMNS.map(c => csvEscape(row[c.key])).join(',')
  );
  return [header, ...lines].join('\r\n');
}

function downloadTextFile(filename, content) {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function exportUnits() {
  const csv = rowsToCsv(filteredUnits.value);
  downloadTextFile(`inventory_${projectId.value}_${Date.now()}.csv`, csv);
}

function downloadTemplate() {
  const example = {
    unit_no: 'A-101',
    tower: 'A',
    floor: '5',
    unit_type: '3BHK Apartment',
    bhk: '3BHK',
    carpet_area: '1200',
    builtup_area: '1450',
    super_builtup_area: '1600',
    balcony_area: '80',
    facing: 'East',
    price: '7500000',
    price_display: '₹75L onwards',
    status: 'available',
    visibility: 'internal',
    customer_shareable: 'false',
    notes: '',
  };
  downloadTextFile('inventory_import_template.csv', rowsToCsv([example]));
}

// ── CSV import + validation preview + bulk import ────────────────────────
const STATUS_VALUES = STATUS_OPTIONS.map(s => s.value);
const VISIBILITY_VALUES = VISIBILITIES.map(v => v.value);

// Minimal RFC4180-ish CSV parser — handles quoted fields with embedded
// commas/newlines/escaped quotes. No external dependency.
function parseCsv(text) {
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i += 1) {
    const char = text[i];
    if (inQuotes) {
      if (char === '"' && text[i + 1] === '"') {
        field += '"';
        i += 1;
      } else if (char === '"') {
        inQuotes = false;
      } else {
        field += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      row.push(field);
      field = '';
    } else if (char === '\n' || char === '\r') {
      if (char === '\r' && text[i + 1] === '\n') i += 1;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
    } else {
      field += char;
    }
  }
  if (field.length || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter(r => r.some(cell => cell.trim() !== ''));
}

function validateImportRow(record) {
  const errors = [];
  if (!record.unit_no || !String(record.unit_no).trim()) {
    errors.push('Unit No is required');
  }
  if (record.status && !STATUS_VALUES.includes(record.status)) {
    errors.push(`Status must be one of: ${STATUS_VALUES.join(', ')}`);
  }
  if (record.visibility && !VISIBILITY_VALUES.includes(record.visibility)) {
    errors.push(`Visibility must be one of: ${VISIBILITY_VALUES.join(', ')}`);
  }
  [
    'floor',
    'carpet_area',
    'builtup_area',
    'super_builtup_area',
    'balcony_area',
    'price',
  ].forEach(key => {
    if (record[key] && Number.isNaN(Number(record[key]))) {
      errors.push(`${key.replace(/_/g, ' ')} must be a number`);
    }
  });
  return errors;
}

const showImportModal = ref(false);
const importFileName = ref('');
const importPreview = ref([]);
const isImporting = ref(false);
const importResult = ref(null);
const importFileInput = ref(null);

const importValidCount = computed(
  () => importPreview.value.filter(r => r.errors.length === 0).length
);

function openImportModal() {
  showImportModal.value = true;
  importFileName.value = '';
  importPreview.value = [];
  importResult.value = null;
}

function closeImportModal() {
  showImportModal.value = false;
}

function onImportFileSelected(e) {
  const file = e.target.files?.[0];
  if (!file) return;
  importFileName.value = file.name;
  importResult.value = null;
  const reader = new FileReader();
  reader.onload = () => {
    const rows = parseCsv(String(reader.result || ''));
    if (!rows.length) {
      importPreview.value = [];
      return;
    }
    const header = rows[0].map(h => h.trim().toLowerCase());
    const keyMap = CSV_COLUMNS.reduce((acc, c) => {
      acc[c.label.toLowerCase()] = c.key;
      return acc;
    }, {});
    const dataRows = rows.slice(1);
    importPreview.value = dataRows.map(cells => {
      const record = {};
      header.forEach((h, idx) => {
        const key = keyMap[h] || h.replace(/\s+/g, '_');
        record[key] = (cells[idx] || '').trim();
      });
      if (record.customer_shareable !== undefined) {
        record.customer_shareable = ['true', '1', 'yes'].includes(
          String(record.customer_shareable).toLowerCase()
        );
      }
      return { record, errors: validateImportRow(record) };
    });
  };
  reader.readAsText(file);
}

async function confirmImport() {
  const validRows = importPreview.value.filter(r => r.errors.length === 0);
  if (!validRows.length) return;
  isImporting.value = true;
  let succeeded = 0;
  const failedRows = [];
  try {
    await validRows.reduce(async (chain, { record }) => {
      await chain;
      const payload = {
        ...record,
        status: record.status || 'available',
        visibility: record.visibility || 'internal',
      };
      try {
        await unitInventoryAPI.create(projectId.value, payload);
        succeeded += 1;
      } catch (e) {
        failedRows.push({
          unit_no: record.unit_no,
          error: e?.response?.data?.errors?.join(', ') || 'Create failed',
        });
      }
    }, Promise.resolve());
    importResult.value = { succeeded, failed: failedRows };
    await load();
  } finally {
    isImporting.value = false;
  }
}
</script>

<template>
  <ProjectWorkspaceLayout
    :show-chrome="isStandalone"
    :project="project"
    :tabs="workspaceTabs"
    active-tab-key="inventory"
  >
    <!-- Header -->
    <div
      class="flex items-center gap-3 px-4 py-3 border-b border-n-weak shrink-0 bg-n-background flex-wrap"
    >
      <span class="i-lucide-layout-grid size-4 text-woot-500 shrink-0" />
      <h2 class="text-sm font-semibold text-n-slate-12">Inventory</h2>

      <div class="flex-1" />

      <router-link
        v-if="isAdmin"
        :to="{
          name: 'nexceo_project_floor_plans',
          params: { id: route.params.id, accountId },
        }"
        class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-n-weak text-n-slate-9 hover:bg-n-alpha-2 transition-colors"
      >
        <span class="i-lucide-map size-3.5" />
        Floor Plans
      </router-link>
      <button
        v-if="isAdmin"
        class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-n-weak text-n-slate-9 hover:bg-n-alpha-2 transition-colors"
        @click="downloadTemplate"
      >
        <span class="i-lucide-file-down size-3.5" />
        Template
      </button>
      <button
        v-if="isAdmin"
        class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-n-weak text-n-slate-9 hover:bg-n-alpha-2 transition-colors"
        @click="openImportModal"
      >
        <span class="i-lucide-upload size-3.5" />
        Import
      </button>
      <button
        class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-n-weak text-n-slate-9 hover:bg-n-alpha-2 transition-colors"
        @click="exportUnits"
      >
        <span class="i-lucide-download size-3.5" />
        Export
      </button>
      <button
        v-if="isAdmin"
        class="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-woot-500 text-white hover:bg-woot-600 transition-colors"
        @click="openNew"
      >
        <span class="i-lucide-plus size-3.5" />
        Add Unit
      </button>
    </div>

    <!-- Error -->
    <div
      v-if="error"
      class="mx-4 mt-3 px-3 py-2 rounded-lg bg-ruby-50 dark:bg-ruby-500/10 border border-ruby-200 dark:border-ruby-500/20 text-xs text-ruby-700 dark:text-ruby-400 flex items-center gap-2 shrink-0"
    >
      <span class="i-lucide-alert-circle size-3.5" />
      {{ error }}
      <button
        class="ml-auto i-lucide-x size-3 opacity-60"
        @click="error = null"
      />
    </div>

    <!-- Summary cards + filters — fixed height, never scrolls, so the table
         below always keeps maximum viewport space (M29.5.20A). -->
    <div class="shrink-0 px-4 pt-3 flex flex-col gap-3">
      <!-- Status summary cards — exactly 6 in one row on desktop. Tailwind's
           grid-cols-N utilities compile to grid-template-columns: repeat(N,
           minmax(0, 1fr)) directly — no arbitrary CSS needed. Previously
           this grid held 7 cards (including Available Value), so 6 filled
           one row and the 7th wrapped alone; splitting Available Value into
           its own card below fixes that at the data level, not just CSS. -->
      <div class="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2.5">
        <div
          v-for="card in STATUS_SUMMARY_CARDS"
          :key="card.label"
          class="flex items-center gap-2.5 px-3 py-2.5 rounded-xl border border-n-weak bg-n-solid-1"
        >
          <div
            class="flex items-center justify-center w-8 h-8 rounded-lg shrink-0"
            :style="{ backgroundColor: card.color + '18' }"
          >
            <span
              class="size-4"
              :class="card.icon"
              :style="{ color: card.color }"
            />
          </div>
          <div class="min-w-0">
            <p class="text-[11px] text-n-slate-7 leading-tight truncate">
              {{ card.label }}
            </p>
            <p
              class="text-base font-bold leading-tight mt-0.5 text-n-slate-12 truncate"
            >
              {{ card.value }}
            </p>
            <p class="text-[10px] text-n-slate-7 leading-tight truncate">
              {{ card.subtitle }}
            </p>
          </div>
        </div>
      </div>

      <!-- Available Value — separate full-width card, never a 7th grid slot -->
      <div
        class="flex items-center gap-3 px-4 py-2.5 rounded-xl border border-n-weak bg-n-solid-1"
      >
        <div
          class="flex items-center justify-center w-8 h-8 rounded-lg shrink-0 bg-sky-50 dark:bg-sky-500/10"
        >
          <span
            class="i-lucide-indian-rupee size-4 text-sky-600 dark:text-sky-400"
          />
        </div>
        <div class="min-w-0 flex items-baseline gap-2 flex-wrap">
          <p class="text-[11px] text-n-slate-7 shrink-0">Available Value</p>
          <p class="text-lg font-bold text-n-slate-12 shrink-0">
            {{ formatCr(summary.totalValue) }}
          </p>
          <p class="text-[11px] text-n-slate-7 truncate">
            Total value of available units
          </p>
        </div>
      </div>

      <!-- Filters -->
      <div class="rounded-xl border border-n-weak bg-n-background p-2.5">
        <div class="flex items-center gap-2 flex-wrap">
          <div class="relative flex-1 min-w-[220px]">
            <span
              class="absolute left-2.5 top-1/2 -translate-y-1/2 i-lucide-search size-3.5 text-n-slate-7 pointer-events-none"
            />
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search unit no, tower, unit type, facing…"
              class="w-full pl-8 pr-3 py-1.5 text-xs border border-n-weak rounded-lg bg-n-background text-n-slate-11 outline-none focus:border-woot-500"
            />
          </div>
          <select
            v-if="uniqueTower.length > 0"
            v-model="towerFilter"
            class="text-xs border border-n-weak rounded-lg px-2 py-1.5 bg-n-background text-n-slate-11 outline-none focus:border-woot-500 w-28"
          >
            <option value="">All towers</option>
            <option v-for="t in uniqueTower" :key="t" :value="t">
              {{ t }}
            </option>
          </select>
          <select
            v-if="uniqueUnitType.length > 0"
            v-model="unitTypeFilter"
            class="text-xs border border-n-weak rounded-lg px-2 py-1.5 bg-n-background text-n-slate-11 outline-none focus:border-woot-500 w-32"
          >
            <option value="">All unit types</option>
            <option v-for="t in uniqueUnitType" :key="t" :value="t">
              {{ t }}
            </option>
          </select>
          <select
            v-if="uniqueBhk.length > 0"
            v-model="bhkFilter"
            class="text-xs border border-n-weak rounded-lg px-2 py-1.5 bg-n-background text-n-slate-11 outline-none focus:border-woot-500 w-28"
          >
            <option value="">All BHK</option>
            <option v-for="bhk in uniqueBhk" :key="bhk" :value="bhk">
              {{ bhk }}
            </option>
          </select>
          <select
            v-model="statusFilter"
            class="text-xs border border-n-weak rounded-lg px-2 py-1.5 bg-n-background text-n-slate-11 outline-none focus:border-woot-500 w-32"
          >
            <option value="">All statuses</option>
            <option v-for="s in STATUS_OPTIONS" :key="s.value" :value="s.value">
              {{ s.label }}
            </option>
          </select>
          <select
            v-if="uniqueFacing.length > 0"
            v-model="facingFilter"
            class="text-xs border border-n-weak rounded-lg px-2 py-1.5 bg-n-background text-n-slate-11 outline-none focus:border-woot-500 w-28"
          >
            <option value="">All facing</option>
            <option v-for="f in uniqueFacing" :key="f" :value="f">
              {{ f }}
            </option>
          </select>
          <button
            v-if="hasActiveFilters"
            class="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg text-ruby-600 dark:text-ruby-400 hover:bg-ruby-50 dark:hover:bg-ruby-500/10 transition-colors shrink-0"
            @click="clearFilters"
          >
            <span class="i-lucide-x size-3.5" />
            Clear
          </button>
          <button
            class="flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-lg border border-n-weak text-n-slate-9 hover:bg-n-alpha-2 transition-colors shrink-0"
            @click="showAdvancedFilters = !showAdvancedFilters"
          >
            <span class="i-lucide-sliders-horizontal size-3.5" />
            More filters
            <span
              class="i-lucide-chevron-down size-3 transition-transform"
              :class="{ '-rotate-180': showAdvancedFilters }"
            />
          </button>
        </div>

        <div
          v-if="showAdvancedFilters"
          class="flex items-center gap-2 flex-wrap mt-2.5 pt-2.5 border-t border-n-weak"
        >
          <select
            v-model="visibilityFilter"
            class="text-xs border border-n-weak rounded-lg px-2 py-1.5 bg-n-background text-n-slate-11 outline-none focus:border-woot-500"
          >
            <option value="">All visibilities</option>
            <option v-for="v in VISIBILITIES" :key="v.value" :value="v.value">
              {{ v.label }}
            </option>
          </select>
          <input
            v-model="minPriceFilter"
            type="number"
            placeholder="Min price (₹)"
            class="text-xs border border-n-weak rounded-lg px-2 py-1.5 bg-n-background text-n-slate-11 outline-none focus:border-woot-500 w-32"
          />
          <input
            v-model="maxPriceFilter"
            type="number"
            placeholder="Max price (₹)"
            class="text-xs border border-n-weak rounded-lg px-2 py-1.5 bg-n-background text-n-slate-11 outline-none focus:border-woot-500 w-32"
          />
        </div>
      </div>

      <!-- Bulk action bar -->
      <div
        v-if="selectedIds.size > 0"
        class="flex items-center gap-2 flex-wrap rounded-xl border border-woot-200 dark:border-woot-500/30 bg-woot-50 dark:bg-woot-500/10 p-3"
      >
        <span class="text-xs font-medium text-woot-700 dark:text-woot-400">
          {{ selectedIds.size }} selected
        </span>
        <div class="flex-1" />
        <button
          v-if="isManagerOrAdmin"
          :disabled="isBulkWorking"
          class="px-2.5 py-1.5 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-9 hover:bg-n-alpha-2 disabled:opacity-50 transition-colors"
          @click="bulkSetStatus('available')"
        >
          Mark Available
        </button>
        <button
          v-if="isManagerOrAdmin"
          :disabled="isBulkWorking"
          class="px-2.5 py-1.5 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-9 hover:bg-n-alpha-2 disabled:opacity-50 transition-colors"
          @click="bulkSetStatus('hold')"
        >
          Mark Hold
        </button>
        <button
          v-if="isManagerOrAdmin"
          :disabled="isBulkWorking"
          class="px-2.5 py-1.5 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-9 hover:bg-n-alpha-2 disabled:opacity-50 transition-colors"
          @click="bulkSetStatus('blocked')"
        >
          Mark Blocked
        </button>
        <button
          v-if="isAdmin"
          :disabled="isBulkWorking"
          class="px-2.5 py-1.5 text-xs rounded-lg border border-ruby-200 dark:border-ruby-500/30 bg-n-background text-ruby-600 dark:text-ruby-400 hover:bg-ruby-50 dark:hover:bg-ruby-500/10 disabled:opacity-50 transition-colors"
          @click="bulkDelete"
        >
          Delete
        </button>
        <button
          class="px-2.5 py-1.5 text-xs rounded-lg text-n-slate-8 hover:text-n-slate-11 transition-colors"
          @click="clearSelection"
        >
          Clear
        </button>
      </div>
    </div>

    <!-- Form region (M29.5.20C) — occupies the SAME flex-1 slot the table
         region below uses (v-if/v-else, mutually exclusive), instead of
         living inside the shrink-0 header/cards/filters column above. That
         placement was the actual bug in M29.5.20B: shrink-0 means "never
         shrink to fit available space," so a shrink-0 form — even with its
         own max-h-[60vh] cap — still added its full height on top of
         whatever the header/cards/filters column already consumed, with
         nothing to force the total back inside one viewport; on a shorter
         window that pushed the footer below the fold regardless of the cap.
         Mirroring the table's own already-correct flex-1 min-h-0
         overflow-hidden wrapper means the form gets exactly the real
         remaining viewport height — no vh guess — so scrolling only ever
         happens in the ONE inner region that's actually supposed to scroll
         (the field list), and the footer, as a shrink-0 sibling after it,
         can never end up below the fold. -->
    <div
      v-if="isAdmin && showForm"
      class="flex-1 min-h-0 px-4 pb-4 pt-3 flex flex-col overflow-hidden"
    >
      <div
        class="flex-1 min-h-0 flex flex-col rounded-xl border border-n-weak bg-n-solid-1 overflow-hidden"
      >
        <p
          class="shrink-0 text-sm font-semibold text-n-slate-12 px-4 pt-4 pb-2"
        >
          {{ editingUnit ? 'Edit Unit' : 'Add Unit' }}
        </p>

        <div class="flex-1 min-h-0 overflow-y-auto px-4 pb-4 space-y-3">
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            <div>
              <label class="block text-xs text-n-slate-8 mb-1">Unit No *</label>
              <input
                v-model="form.unit_no"
                class="w-full text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-11 outline-none focus:border-woot-500"
                placeholder="e.g. A-101"
              />
            </div>
            <div>
              <label class="block text-xs text-n-slate-8 mb-1">Tower</label>
              <input
                v-model="form.tower"
                class="w-full text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-11 outline-none focus:border-woot-500"
                placeholder="e.g. A"
              />
            </div>
            <div>
              <label class="block text-xs text-n-slate-8 mb-1">Floor</label>
              <input
                v-model="form.floor"
                type="number"
                class="w-full text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-11 outline-none focus:border-woot-500"
                placeholder="e.g. 5"
              />
            </div>
            <div>
              <label class="block text-xs text-n-slate-8 mb-1">Unit Type</label>
              <input
                v-model="form.unit_type"
                class="w-full text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-11 outline-none focus:border-woot-500"
                placeholder="e.g. 3BHK Apartment"
              />
            </div>
            <div>
              <label class="block text-xs text-n-slate-8 mb-1">BHK</label>
              <input
                v-model="form.bhk"
                class="w-full text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-11 outline-none focus:border-woot-500"
                placeholder="e.g. 3BHK"
              />
            </div>
            <div>
              <label class="block text-xs text-n-slate-8 mb-1"
                >Carpet Area (sq ft)</label
              >
              <input
                v-model="form.carpet_area"
                type="number"
                class="w-full text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-11 outline-none focus:border-woot-500"
                placeholder="e.g. 1200"
              />
            </div>
            <div>
              <label class="block text-xs text-n-slate-8 mb-1"
                >Balcony Area (sq ft)</label
              >
              <input
                v-model="form.balcony_area"
                type="number"
                class="w-full text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-11 outline-none focus:border-woot-500"
                placeholder="e.g. 80"
              />
            </div>
            <div>
              <label class="block text-xs text-n-slate-8 mb-1"
                >Builtup Area (sq ft)</label
              >
              <input
                v-model="form.builtup_area"
                type="number"
                class="w-full text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-11 outline-none focus:border-woot-500"
                placeholder="e.g. 1450"
              />
            </div>
            <div>
              <label class="block text-xs text-n-slate-8 mb-1"
                >Super Builtup Area (sq ft)</label
              >
              <input
                v-model="form.super_builtup_area"
                type="number"
                class="w-full text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-11 outline-none focus:border-woot-500"
                placeholder="e.g. 1600"
              />
            </div>
            <div>
              <label class="block text-xs text-n-slate-8 mb-1">Facing</label>
              <input
                v-model="form.facing"
                class="w-full text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-11 outline-none focus:border-woot-500"
                placeholder="e.g. East"
              />
            </div>
            <div>
              <label class="block text-xs text-n-slate-8 mb-1">Price (₹)</label>
              <input
                v-model="form.price"
                type="number"
                class="w-full text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-11 outline-none focus:border-woot-500"
                placeholder="e.g. 7500000"
              />
            </div>
            <div>
              <label class="block text-xs text-n-slate-8 mb-1"
                >Price Display</label
              >
              <input
                v-model="form.price_display"
                class="w-full text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-11 outline-none focus:border-woot-500"
                placeholder="e.g. ₹75L onwards"
              />
            </div>
            <div>
              <label class="block text-xs text-n-slate-8 mb-1">Status</label>
              <select
                v-model="form.status"
                class="w-full text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-11 outline-none focus:border-woot-500"
              >
                <option
                  v-for="s in STATUS_OPTIONS"
                  :key="s.value"
                  :value="s.value"
                >
                  {{ s.label }}
                </option>
              </select>
            </div>
            <div>
              <label class="block text-xs text-n-slate-8 mb-1"
                >Visibility</label
              >
              <select
                v-model="form.visibility"
                class="w-full text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-11 outline-none focus:border-woot-500"
              >
                <option
                  v-for="v in VISIBILITIES"
                  :key="v.value"
                  :value="v.value"
                >
                  {{ v.label }}
                </option>
              </select>
            </div>
          </div>

          <div>
            <div class="flex items-center justify-between mb-1.5">
              <label class="block text-xs text-n-slate-8">
                Specifications
                <span class="text-n-slate-6 font-normal"
                  >(room-by-room sizes shown on the customer-facing
                  Specifications tab)</span
                >
              </label>
              <button
                type="button"
                class="flex items-center gap-1 text-xs font-medium text-woot-600 hover:underline"
                @click="addSpecification"
              >
                <span class="i-lucide-plus size-3" />
                Add Room
              </button>
            </div>
            <div
              v-if="!specifications.length"
              class="text-xs text-n-slate-7 py-2"
            >
              None added yet — e.g. Living/Dining, Kitchen, Bedroom 1,
              Bedroom 2, Bathroom 1, Balcony 1.
            </div>
            <div v-else class="flex flex-col gap-1.5">
              <div
                v-for="(spec, i) in specifications"
                :key="i"
                class="flex items-center gap-2"
              >
                <input
                  v-model="spec.label"
                  type="text"
                  placeholder="Room, e.g. Bedroom 1"
                  class="flex-1 text-xs px-2.5 py-1.5 border border-n-weak rounded-lg bg-n-background text-n-slate-11 outline-none focus:border-woot-500"
                />
                <input
                  v-model="spec.size"
                  type="text"
                  placeholder="Size, e.g. 10'8&quot; x 10'4&quot;"
                  class="flex-1 text-xs px-2.5 py-1.5 border border-n-weak rounded-lg bg-n-background text-n-slate-11 outline-none focus:border-woot-500"
                />
                <button
                  type="button"
                  class="flex items-center justify-center w-7 h-7 rounded-lg text-n-slate-7 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10 transition-colors shrink-0"
                  @click="removeSpecification(i)"
                >
                  <span class="i-lucide-x size-3.5" />
                </button>
              </div>
            </div>
          </div>

          <div>
            <label class="block text-xs text-n-slate-8 mb-1">Notes</label>
            <textarea
              v-model="form.notes"
              rows="2"
              class="w-full text-sm px-3 py-2 border border-n-weak rounded-lg bg-n-background text-n-slate-11 outline-none focus:border-woot-500 resize-none"
              placeholder="Internal notes (not shown to customer)"
            />
          </div>

          <label
            class="flex items-center gap-2 cursor-pointer text-xs text-n-slate-9"
          >
            <input
              v-model="form.customer_shareable"
              type="checkbox"
              class="rounded"
            />
            Customer shareable (AI may reference this unit in summary counts)
          </label>

          <div
            v-if="saveError"
            class="text-xs text-ruby-600 bg-ruby-50 dark:bg-ruby-500/10 px-3 py-2 rounded-lg border border-ruby-200 dark:border-ruby-500/20"
          >
            {{ saveError }}
          </div>
        </div>

        <!-- Docked action footer — a plain flex sibling after the scrollable
             field area above, never a scroll participant itself, so it can
             never be scrolled out of view and can never overlap the last
             field. Update Unit stays the primary action; Cancel is
             secondary; same handlers as before. -->
        <div
          class="shrink-0 flex gap-2 p-4 border-t border-n-weak bg-n-solid-1"
        >
          <button
            :disabled="isSaving"
            class="flex-1 py-2 text-sm rounded-lg bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-50 transition-colors"
            @click="save"
          >
            {{
              isSaving ? 'Saving…' : editingUnit ? 'Update Unit' : 'Add Unit'
            }}
          </button>
          <button
            class="flex-1 py-2 text-sm rounded-lg border border-n-weak text-n-slate-8 hover:border-n-strong transition-colors"
            @click="closeForm"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>

    <!-- Table region — the one part of the page that scrolls, and gets
           all remaining viewport height (M29.5.20A). Now mutually exclusive
           with the Form region above via v-else (M29.5.20C) — only one of
           the two ever occupies this flex-1 slot at a time. -->
    <div
      v-else
      class="flex-1 min-h-0 px-4 pb-4 pt-3 flex flex-col overflow-hidden"
    >
      <!-- Loading -->
      <div
        v-if="isLoading && !units.length"
        class="flex items-center justify-center py-16"
      >
        <div class="text-center">
          <span
            class="i-lucide-loader-2 size-8 animate-spin text-woot-500 mx-auto block mb-3"
          />
          <p class="text-sm text-n-slate-8">Loading inventory</p>
        </div>
      </div>

      <!-- Empty -->
      <div
        v-else-if="!units.length && !isLoading"
        class="flex items-center justify-center py-16"
      >
        <div class="text-center max-w-xs">
          <div
            class="mx-auto mb-4 flex items-center justify-center w-14 h-14 rounded-2xl bg-woot-50 dark:bg-woot-500/10"
          >
            <span class="i-lucide-layout-grid size-7 text-woot-500" />
          </div>
          <p class="text-sm font-medium text-n-slate-11 mb-1">
            No units added yet
          </p>
          <p class="text-xs text-n-slate-7 mb-4">
            Add units to track availability and let the AI check and hold units
            on your behalf.
          </p>
          <button
            v-if="isAdmin"
            class="px-4 py-2 text-sm rounded-lg bg-woot-500 text-white hover:bg-woot-600 transition-colors"
            @click="openNew"
          >
            Add First Unit
          </button>
        </div>
      </div>

      <!-- Table -->
      <div
        v-else
        class="flex-1 min-h-0 flex flex-col rounded-xl border border-n-weak overflow-hidden"
      >
        <div class="flex-1 min-h-0 overflow-auto">
          <table class="w-full text-sm border-collapse">
            <thead>
              <tr class="text-left text-xs text-n-slate-7">
                <th
                  class="sticky top-0 z-10 bg-n-solid-1 border-b border-n-weak py-2 pl-3 pr-2 font-medium w-8"
                >
                  <input
                    type="checkbox"
                    class="rounded"
                    :checked="allVisibleSelected"
                    @change="toggleSelectAllVisible"
                  />
                </th>
                <th
                  class="sticky top-0 z-10 bg-n-solid-1 border-b border-n-weak py-2 pr-3 font-medium"
                >
                  Unit
                </th>
                <th
                  class="sticky top-0 z-10 bg-n-solid-1 border-b border-n-weak py-2 pr-3 font-medium"
                >
                  Tower/Floor
                </th>
                <th
                  class="sticky top-0 z-10 bg-n-solid-1 border-b border-n-weak py-2 pr-3 font-medium"
                >
                  Unit Type
                </th>
                <th
                  class="sticky top-0 z-10 bg-n-solid-1 border-b border-n-weak py-2 pr-3 font-medium"
                >
                  BHK
                </th>
                <th
                  class="sticky top-0 z-10 bg-n-solid-1 border-b border-n-weak py-2 pr-3 font-medium text-right"
                >
                  Carpet Area
                </th>
                <th
                  class="sticky top-0 z-10 bg-n-solid-1 border-b border-n-weak py-2 pr-3 font-medium text-right"
                >
                  Balcony Area
                </th>
                <th
                  class="sticky top-0 z-10 bg-n-solid-1 border-b border-n-weak py-2 pr-3 font-medium text-right"
                >
                  BUA
                </th>
                <th
                  class="sticky top-0 z-10 bg-n-solid-1 border-b border-n-weak py-2 pr-3 font-medium text-right"
                >
                  SBA
                </th>
                <th
                  class="sticky top-0 z-10 bg-n-solid-1 border-b border-n-weak py-2 pr-3 font-medium"
                >
                  Facing
                </th>
                <th
                  class="sticky top-0 z-10 bg-n-solid-1 border-b border-n-weak py-2 pr-3 font-medium text-right"
                >
                  Price
                </th>
                <th
                  class="sticky top-0 z-10 bg-n-solid-1 border-b border-n-weak py-2 pr-3 font-medium"
                >
                  Status
                </th>
                <th
                  class="sticky top-0 z-10 bg-n-solid-1 border-b border-n-weak py-2 pr-3 font-medium"
                >
                  Visibility
                </th>
                <th
                  class="sticky top-0 z-10 bg-n-solid-1 border-b border-n-weak py-2 pr-3 font-medium"
                >
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="unit in filteredUnits"
                :key="unit.id"
                class="border-b border-n-weak/50 hover:bg-n-alpha-1 transition-colors"
                :class="{
                  'bg-woot-50 dark:bg-woot-500/5': selectedIds.has(unit.id),
                }"
              >
                <td class="py-3 pl-3 pr-2">
                  <input
                    type="checkbox"
                    class="rounded"
                    :checked="selectedIds.has(unit.id)"
                    @change="toggleSelect(unit.id)"
                  />
                </td>
                <td class="py-3 pr-3">
                  <span class="font-medium text-n-slate-12">{{
                    unit.unit_no
                  }}</span>
                </td>
                <td class="py-3 pr-3 text-xs text-n-slate-7">
                  {{
                    [
                      unit.tower && `Tower ${unit.tower}`,
                      unit.floor && `Fl ${unit.floor}`,
                    ]
                      .filter(Boolean)
                      .join(' · ') || '—'
                  }}
                </td>
                <td class="py-3 pr-3 text-n-slate-9">
                  {{ unit.unit_type || '—' }}
                </td>
                <td class="py-3 pr-3 text-n-slate-9">
                  {{ unit.bhk || '—' }}
                </td>
                <td
                  class="py-3 pr-3 text-n-slate-9 text-xs text-right tabular-nums"
                >
                  {{ unit.carpet_area ? `${unit.carpet_area} sqft` : '—' }}
                </td>
                <td
                  class="py-3 pr-3 text-n-slate-9 text-xs text-right tabular-nums"
                >
                  {{ unit.balcony_area ? `${unit.balcony_area} sqft` : '—' }}
                </td>
                <td
                  class="py-3 pr-3 text-n-slate-9 text-xs text-right tabular-nums"
                >
                  {{ unit.builtup_area ? `${unit.builtup_area} sqft` : '—' }}
                </td>
                <td
                  class="py-3 pr-3 text-n-slate-9 text-xs text-right tabular-nums"
                >
                  {{
                    unit.super_builtup_area
                      ? `${unit.super_builtup_area} sqft`
                      : '—'
                  }}
                </td>
                <td class="py-3 pr-3 text-n-slate-9 text-xs">
                  {{ unit.facing || '—' }}
                </td>
                <td
                  class="py-3 pr-3 text-n-slate-9 font-medium text-right tabular-nums"
                >
                  {{ priceLabel(unit) }}
                </td>
                <td class="py-3 pr-3">
                  <span
                    class="px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize"
                    :style="{
                      backgroundColor: statusColor(unit.status) + '22',
                      color: statusColor(unit.status),
                    }"
                  >
                    {{ statusLabel(unit.status) }}
                  </span>
                </td>
                <td class="py-3 pr-3 text-xs text-n-slate-7 capitalize">
                  {{ unit.visibility?.replace(/_/g, ' ') || '—' }}
                </td>
                <td class="py-3 pr-3">
                  <div class="flex items-center gap-1">
                    <!-- Status quick-set -->
                    <select
                      v-if="isManagerOrAdmin && unit.status !== 'sold'"
                      :value="unit.status"
                      class="text-xs border border-n-weak rounded px-1.5 py-1 bg-n-background text-n-slate-9 outline-none"
                      @change="e => setStatus(unit, e.target.value)"
                    >
                      <option value="available">Available</option>
                      <option value="hold">Hold</option>
                      <option value="blocked">Blocked</option>
                      <option value="sold">Sold</option>
                    </select>
                    <button
                      v-if="isAdmin"
                      class="p-1 rounded text-n-slate-7 hover:text-woot-500 hover:bg-woot-50 dark:hover:bg-woot-500/10 transition-colors"
                      title="Edit"
                      @click="openEdit(unit)"
                    >
                      <span class="i-lucide-pencil size-3.5" />
                    </button>
                    <button
                      v-if="isAdmin"
                      class="p-1 rounded text-n-slate-7 hover:text-ruby-500 hover:bg-ruby-50 dark:hover:bg-ruby-500/10 transition-colors"
                      title="Delete"
                      @click="confirmDelete = unit"
                    >
                      <span class="i-lucide-trash-2 size-3.5" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <p
          v-if="filteredUnits.length === 0 && units.length > 0"
          class="text-center text-xs text-n-slate-7 py-6"
        >
          No units match the current filters.
        </p>
      </div>
    </div>

    <!-- Import modal -->
    <div
      v-if="isAdmin && showImportModal"
      class="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
      @click.self="closeImportModal"
    >
      <div
        class="bg-n-solid-1 rounded-xl border border-n-weak p-5 max-w-2xl w-full max-h-[85vh] overflow-y-auto space-y-4"
      >
        <div class="flex items-center justify-between">
          <p class="text-sm font-semibold text-n-slate-12">
            Import Units from CSV
          </p>
          <button
            class="i-lucide-x size-4 text-n-slate-7"
            @click="closeImportModal"
          />
        </div>

        <p class="text-xs text-n-slate-8">
          Upload a CSV file matching the
          <button
            class="text-woot-600 dark:text-woot-400 underline"
            @click="downloadTemplate"
          >
            import template</button
          >. Each row is validated before import — only valid rows are created.
        </p>

        <input
          ref="importFileInput"
          type="file"
          accept=".csv,text/csv"
          class="w-full text-xs file:mr-3 file:px-3 file:py-1.5 file:rounded-lg file:border file:border-n-weak file:bg-n-background file:text-n-slate-9 file:text-xs"
          @change="onImportFileSelected"
        />

        <div v-if="importFileName && importPreview.length" class="space-y-2">
          <div class="flex items-center gap-2 text-xs">
            <span class="text-n-slate-8"
              >{{ importFileName }} · {{ importPreview.length }} rows</span
            >
            <span
              class="px-1.5 py-0.5 rounded-full font-medium bg-success-50 text-success-800 dark:bg-success-800/20 dark:text-success-400"
            >
              {{ importValidCount }} valid
            </span>
            <span
              v-if="importPreview.length - importValidCount > 0"
              class="px-1.5 py-0.5 rounded-full font-medium bg-ruby-50 text-ruby-700 dark:bg-ruby-500/10 dark:text-ruby-400"
            >
              {{ importPreview.length - importValidCount }} invalid
            </span>
          </div>

          <div class="max-h-64 overflow-y-auto rounded-lg border border-n-weak">
            <table class="w-full text-xs border-collapse">
              <thead>
                <tr class="text-left text-n-slate-7 bg-n-alpha-1">
                  <th class="py-1.5 px-2 font-medium">Unit No</th>
                  <th class="py-1.5 px-2 font-medium">Status</th>
                  <th class="py-1.5 px-2 font-medium">Price</th>
                  <th class="py-1.5 px-2 font-medium">Validation</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(row, idx) in importPreview"
                  :key="idx"
                  class="border-t border-n-weak"
                  :class="
                    row.errors.length ? 'bg-ruby-50/50 dark:bg-ruby-500/5' : ''
                  "
                >
                  <td class="py-1.5 px-2">{{ row.record.unit_no || '—' }}</td>
                  <td class="py-1.5 px-2">
                    {{ row.record.status || 'available' }}
                  </td>
                  <td class="py-1.5 px-2">{{ row.record.price || '—' }}</td>
                  <td class="py-1.5 px-2">
                    <span
                      v-if="!row.errors.length"
                      class="text-success-600 dark:text-success-400"
                    >
                      <span
                        class="i-lucide-check size-3 inline-block mr-0.5"
                      />OK
                    </span>
                    <span v-else class="text-ruby-600 dark:text-ruby-400">
                      {{ row.errors.join('; ') }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div
          v-if="importResult"
          class="text-xs px-3 py-2 rounded-lg border"
          :class="
            importResult.failed.length
              ? 'bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-400'
              : 'bg-success-50 border-success-200 text-success-800 dark:bg-success-800/20 dark:border-success-500/20 dark:text-success-400'
          "
        >
          Imported {{ importResult.succeeded }} unit(s).
          <span v-if="importResult.failed.length">
            {{ importResult.failed.length }} failed:
            {{
              importResult.failed
                .map(f => `${f.unit_no} (${f.error})`)
                .join(', ')
            }}
          </span>
        </div>

        <div class="flex gap-2 justify-end">
          <button
            class="px-4 py-2 text-sm rounded-lg border border-n-weak text-n-slate-9 hover:bg-n-alpha-2 transition-colors"
            @click="closeImportModal"
          >
            Close
          </button>
          <button
            v-if="importValidCount > 0"
            :disabled="isImporting"
            class="flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-50 transition-colors"
            @click="confirmImport"
          >
            <span
              v-if="isImporting"
              class="i-lucide-loader-2 size-3.5 animate-spin"
            />
            Import {{ importValidCount }} valid row{{
              importValidCount === 1 ? '' : 's'
            }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete confirm -->
    <div
      v-if="isAdmin && confirmDelete"
      class="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
    >
      <div
        class="bg-n-solid-1 rounded-xl border border-n-weak p-5 max-w-sm w-full space-y-3"
      >
        <p class="text-sm font-semibold text-n-slate-12">Delete unit?</p>
        <p class="text-xs text-n-slate-8">
          Unit <strong>{{ confirmDelete.unit_no }}</strong> will be permanently
          deleted. This cannot be undone.
        </p>
        <div class="flex gap-2">
          <button
            class="flex-1 py-1.5 text-sm rounded-lg bg-ruby-500 text-white hover:bg-ruby-600 transition-colors"
            @click="deleteUnit(confirmDelete)"
          >
            Delete
          </button>
          <button
            class="flex-1 py-1.5 text-sm rounded-lg border border-n-weak text-n-slate-8 hover:border-n-strong transition-colors"
            @click="confirmDelete = null"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  </ProjectWorkspaceLayout>
</template>
